import React, { useEffect, useState } from 'react';
import { isMobile } from '../../utils/mobileUtils';
import {
  SpaceCodeValidationStep,
  UserSignupStep,
} from '../../components/Form/index';
import { Footer } from '../../components/Footer';
import SentSuccessfullyImage from '../../assets/sent_successfully.png';

import {
  Container,
  AulaLogo,
  SpacingContainer,
  ContentContainer,
  EmailLogo,
  EmailMessage,
  EmailSentContainer,
  SigninButton,
  HeaderContainer,
  MessageContainer,
  Loader,
} from './SignupPage.styled';
import { useCodesFromQuery } from '../../hooks/useCodesFromQuery';
import { SuccessMessage } from '../../components/SuccessMessage';

import { useSpace } from '../../hooks/useSpace';
import { useSignup } from '../../hooks/useSignup';
import { useVerifyEmail } from '../../hooks/useVerifyEmail';
import { getInstitutionURL } from '../../utils/getInstitution';
import { windowUtils } from '../../utils/windowUtils';
import { useInstitutionConfig } from '../../hooks/useInstitutionConfig';

type SignupPageProps = {
  code?: string;
};

export const SignupPage: React.FC<SignupPageProps> = () => {
  const { spaceCode, setSpaceCode, verificationCode, clearQuery } =
    useCodesFromQuery();

  const { data: configData } = useInstitutionConfig();

  const {
    data: useSpaceData,
    isLoading: isLoadingSpace,
    isError: spaceCodeError,
  } = useSpace(spaceCode);

  const { name: spaceName } = useSpaceData || { name: '' };

  const {
    mutate: signup,
    isSuccess: isSignupSuccess,
    isLoading: isSigningUp,
    isError: signupError,
  } = useSignup();

  const {
    isLoading: isVerifyingEmail,
    isSuccess: isVerifyEmailSuccess,
    isError: emailVerificationError,
    reset: resetEmailError,
  } = useVerifyEmail({ spaceCode, verificationCode, onMutate: clearQuery });

  const [showLoader, setShowLoader] = useState(false);
  const [isVerificationStep, setIsVerificationStep] = useState(false);

  useEffect(() => {
    if (isVerifyingEmail && isLoadingSpace) {
      setShowLoader(true);
      setIsVerificationStep(true);
    }

    if (!isVerifyingEmail && !isLoadingSpace) {
      setShowLoader(false);
      if (emailVerificationError || spaceCodeError) {
        setIsVerificationStep(false);
      }
    }
  }, [
    isVerifyingEmail,
    isLoadingSpace,
    emailVerificationError,
    spaceCodeError,
  ]);

  const showSigninButton = !(isSignupSuccess || isVerifyEmailSuccess);

  const renderSuccessMessage = (name: string) => (
    <MessageContainer>
      {isVerifyEmailSuccess && <SuccessMessage spaceName={name} />}
      {showLoader && <Loader />}
    </MessageContainer>
  );

  const renderSignupForm = () => {
    if (!spaceCode || !spaceName) {
      return (
        <SpaceCodeValidationStep
          onSubmit={(data) => {
            resetEmailError();
            setSpaceCode(data.spaceCode);
          }}
          loading={isLoadingSpace}
          spaceCode={spaceCode}
          error={spaceCodeError}
        />
      );
    }

    if (isSignupSuccess) {
      return (
        <MessageContainer>
          <EmailSentContainer>
            <EmailLogo src={SentSuccessfullyImage} />
            <EmailMessage>
              Check your email to confirm your account.
            </EmailMessage>
          </EmailSentContainer>
        </MessageContainer>
      );
    }

    return (
      <UserSignupStep
        onSubmit={(data) => {
          resetEmailError();
          signup({
            ...data,
            spaceCode,
          });
        }}
        spaceName={spaceName}
        loading={isSigningUp}
        termsAndConditionsUrl={configData?.termsAndConditions?.url}
        institutionDisplayName={configData?.general.display_name}
        error={signupError || emailVerificationError}
      />
    );
  };

  return (
    <Container>
      {!isMobile() && (
        <SpacingContainer>
          <AulaLogo />
        </SpacingContainer>
      )}
      <ContentContainer>
        <HeaderContainer>
          {isMobile() && <AulaLogo />}
          {showSigninButton && (
            <SigninButton
              id="signin-button"
              type={isMobile() ? 'text' : 'secondary'}
              onClick={() => windowUtils.open(getInstitutionURL())}
            >
              Sign in
            </SigninButton>
          )}
        </HeaderContainer>
        {isVerificationStep
          ? renderSuccessMessage(spaceName)
          : renderSignupForm()}
      </ContentContainer>
      {isMobile() && !showSigninButton && <Footer />}
    </Container>
  );
};
